import React, { useEffect, useMemo, useRef, useState } from 'react';

type TimerMode = 'up' | 'down';

type TimerProps = {
  mode: TimerMode;

  initialSeconds?: number;

  endAtMs?: number;
  startAtMs?: number;

  nowProvider?: () => number;

  running?: boolean;
  tickMs?: number;

  intervalSeconds?: number;
  onInterval?: (payload: { seconds: number; mode: TimerMode }) => void;

  onExpire?: () => void;

  resetOnInitialChange?: boolean;

  className?: string;
  format?: (seconds: number, mode: TimerMode) => string;
};

function defaultFormat(seconds: number) {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  const pad = (n: number) => String(n).padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}

export function Timer({
  mode,
  initialSeconds,
  endAtMs,
  startAtMs,
  nowProvider,
  running = true,
  tickMs = 1000,
  intervalSeconds,
  onInterval,
  onExpire,
  resetOnInitialChange = true,
  className = '',
  format,
}: TimerProps) {
  const getNowMs = useMemo(
    () => nowProvider ?? (() => Date.now()),
    [nowProvider]
  );

  const isAbsolute =
    (mode === 'down' && typeof endAtMs === 'number') ||
    (mode === 'up' && typeof startAtMs === 'number');

  const computeSeconds = useMemo(() => {
    return () => {
      if (isAbsolute) {
        const now = getNowMs();
        if (mode === 'down')
          return Math.max(0, ((endAtMs as number) - now) / 1000);
        return Math.max(0, (now - (startAtMs as number)) / 1000);
      }
      return Math.max(0, initialSeconds ?? 0);
    };
  }, [isAbsolute, mode, endAtMs, startAtMs, initialSeconds, getNowMs]);

  const initial = useMemo(() => computeSeconds(), [computeSeconds]);

  const [seconds, setSeconds] = useState<number>(initial);

  // ---- one-shot guards ----
  const expiredRef = useRef(false); // prevents repeats
  const armedRef = useRef(false); // only allow expire if we started > 0
  const lastInitialKeyRef = useRef<string>(''); // helps reset guards reliably

  const accMsRef = useRef(0);
  const lastTsRef = useRef<number | null>(null);
  const intervalAccRef = useRef(0);

  // Reset when inputs change
  useEffect(() => {
    if (!resetOnInitialChange) return;

    const key = JSON.stringify({
      mode,
      initialSeconds,
      endAtMs,
      startAtMs,
    });

    // avoid over-resetting if parent re-renders with same values
    if (lastInitialKeyRef.current === key) return;
    lastInitialKeyRef.current = key;

    const init = computeSeconds();
    setSeconds(init);

    expiredRef.current = false;
    armedRef.current = init > 0;

    accMsRef.current = 0;
    intervalAccRef.current = 0;
    lastTsRef.current = null;
  }, [
    mode,
    initialSeconds,
    endAtMs,
    startAtMs,
    computeSeconds,
    resetOnInitialChange,
  ]);

  const tryExpireNow = () => {
    if (!running) return;
    if (mode !== 'down') return;
    if (expiredRef.current) return;
    if (!armedRef.current) return;

    const s = computeSeconds();
    if (s <= 0) {
      expiredRef.current = true;
      armedRef.current = false;
      onExpire?.();
      setSeconds(0);
    }
  };

  // ✅ Critical: if tab was inactive, check on resume (and on mount)
  useEffect(() => {
    tryExpireNow();

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        tryExpireNow();
      }
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', tryExpireNow);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', tryExpireNow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, mode, onExpire, computeSeconds]);

  // display ticking (rAF) — fine to pause in background; we’ll catch up on focus/visibility
  useEffect(() => {
    if (!running) {
      lastTsRef.current = null;
      return;
    }

    let rafId = 0;

    const step = (ts: number) => {
      if (!running) return;

      if (lastTsRef.current == null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;

      accMsRef.current += delta;

      while (accMsRef.current >= tickMs) {
        accMsRef.current -= tickMs;

        setSeconds((prev) => {
          const next = isAbsolute
            ? computeSeconds()
            : mode === 'up'
            ? prev + tickMs / 1000
            : Math.max(0, prev - tickMs / 1000);

          if (intervalSeconds && onInterval) {
            intervalAccRef.current += tickMs / 1000;
            if (intervalAccRef.current >= intervalSeconds) {
              intervalAccRef.current = intervalAccRef.current % intervalSeconds;
              onInterval({ seconds: next, mode });
            }
          }

          // expire (also guarded)
          if (mode === 'down' && next <= 0) {
            // tryExpireNow() will enforce the once-only + armed rules
            // but we can also attempt here for foreground accuracy
            if (!expiredRef.current && armedRef.current) {
              expiredRef.current = true;
              armedRef.current = false;
              onExpire?.();
              return 0;
            }
          }

          return next;
        });
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [
    running,
    tickMs,
    mode,
    intervalSeconds,
    onInterval,
    onExpire,
    isAbsolute,
    computeSeconds,
  ]);

  const text = (format ?? ((s) => defaultFormat(s)))(seconds, mode);

  return (
    <span className={['tabular-nums', className].join(' ')} aria-live="polite">
      {text}
    </span>
  );
}
